import { useEffect, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  StaffLogoutNoticeDialog,
  useStaffLogoutFlow,
} from "@/components/shared/StaffLogoutNotice";

import { listEmployees } from "@/lib/api/employees.functions";
import {
  setStaffPin,
  verifyStaffPin,
  ensureOwnerPin,
  ensureBootstrapAdmin,
  setStaffActive,
  resetOwnPinWithPassword,
  requestPinResetCode,
  resetOwnPinWithCode,
} from "@/lib/api/staff-pin.functions";
import { ROLE_OPTIONS } from "@/lib/permissions-catalog";

import { ACTIVE_STAFF_KEY, ACTIVE_STAFF_NAME_KEY } from "@/lib/active-staff";

export const Route = createFileRoute("/_authenticated/selecionar-perfil")({
  component: ProfilePickerPage,
});

type Profile = {
  id: string;
  name: string | null;
  role: string;
  login: string | null;
  photo_url: string | null;
  hasPin?: boolean;
  isActive?: boolean;
  isBootstrapAdmin?: boolean;
};


function roleLabel(role: string) {
  return ROLE_OPTIONS.find((r) => r.value === role)?.label ?? role;
}

function ProfilePickerPage() {
  const navigate = useNavigate();
  const list = useServerFn(listEmployees);
  const verify = useServerFn(verifyStaffPin);
  const setPinFn = useServerFn(setStaffPin);
  const ensureOwner = useServerFn(ensureOwnerPin);
  const ensureAdmin = useServerFn(ensureBootstrapAdmin);
  const setActive = useServerFn(setStaffActive);
  const resetWithPassword = useServerFn(resetOwnPinWithPassword);
  const requestCode = useServerFn(requestPinResetCode);
  const resetWithCode = useServerFn(resetOwnPinWithCode);

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [ownerName, setOwnerName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [pinIsDefault, setPinIsDefault] = useState(false);
  const [isOwnerAccount, setIsOwnerAccount] = useState(false);

  const [selected, setSelected] = useState<Profile | null>(null);
  const [mode, setMode] = useState<"verify" | "create">("verify");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // Fluxo pós-login com PIN padrão
  const [afterDefaultLogin, setAfterDefaultLogin] = useState<{ id: string; name: string | null } | null>(null);
  // Fluxo "Esqueci meu PIN" (proprietário)
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotMode, setForgotMode] = useState<"password" | "code">("password");
  const [accountPassword, setAccountPassword] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [newPin, setNewPin] = useState("");
  const [newPinConfirm, setNewPinConfirm] = useState("");

  useEffect(() => {
    (async () => {
      try {
        let ownerPin: any = null;
        try {
          ownerPin = await ensureOwner({ data: undefined as any });
        } catch {
          ownerPin = null;
        }
        let adminRes: any = null;
        try {
          adminRes = await ensureAdmin({ data: undefined as any });
        } catch {
          adminRes = null;
        }
        const res = await list();
        const list2 = ((res.employees as Profile[]) ?? [])
          .filter((p) => p.isActive !== false)
          .slice();
        const ownId = (res as any).ownerId ?? ownerPin?.ownerId ?? null;
        const ownNm = (res as any).ownerName ?? ownerPin?.ownerName ?? null;
        setOwnerId(ownId);
        setOwnerName(ownNm);
        setPinIsDefault(!!ownerPin?.pinIsDefault);
        setIsOwnerAccount(!!ownerPin?.isOwner);
        // Garante o dono na lista, mesmo que não esteja em "profiles"
        if (ownId && ownerPin?.isOwner !== false && !list2.find((p) => p.id === ownId)) {
          list2.unshift({
            id: ownId,
            name: ownNm,
            role: "proprietario",
            login: null,
            photo_url: null,
            hasPin: !!ownerPin?.hasPin,
          });
        } else if (ownId && ownerPin?.hasPin) {
          const idx = list2.findIndex((p) => p.id === ownId);
          if (idx >= 0) list2[idx] = { ...list2[idx], hasPin: true };
        }

        // Card fixo do Administrador (primeiro acesso)
        const adm = adminRes?.admin;
        if (adm && adm.isActive !== false) {
          const idx = list2.findIndex((p) => p.id === adm.id);
          const card: Profile = {
            id: adm.id,
            name: adm.name,
            role: adm.role,
            login: null,
            photo_url: adm.photo_url ?? null,
            hasPin: !!adm.hasPin,
            isActive: true,
            isBootstrapAdmin: true,
          };
          if (idx >= 0) list2[idx] = { ...list2[idx], ...card };
          else list2.push(card);
        }
        setProfiles(list2);
      } catch (e: any) {
        toast.error(e?.message || "Erro ao carregar perfis");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onDeactivateAdmin = async (id: string) => {
    setBusy(true);
    try {
      await setActive({ data: { staffId: id, active: false } });
      setProfiles((prev) => prev.filter((p) => p.id !== id));
      toast.success("Card de Administrador inativado. Você pode reativá-lo em Contas de acesso.");
    } catch (e: any) {
      toast.error(e?.message || "Não foi possível inativar o card.");
    } finally {
      setBusy(false);
    }
  };




  const enterAs = (id: string, name: string | null) => {
    localStorage.setItem(ACTIVE_STAFF_KEY, id);
    localStorage.setItem(ACTIVE_STAFF_NAME_KEY, name ?? "");
    navigate({ to: "/dashboard" });
  };

  const onClickProfile = (p: Profile) => {
    setSelected(p);
    setPin("");
    setPinConfirm("");
    setMode(p.hasPin ? "verify" : "create");
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    setBusy(true);
    try {
      if (mode === "create") {
        if (!/^\d{4,6}$/.test(pin)) throw new Error("O PIN deve ter de 4 a 6 dígitos numéricos.");
        if (pin !== pinConfirm) throw new Error("Os PINs não conferem.");
        await setPinFn({ data: { staffId: selected.id, pin } });
        toast.success("PIN definido com sucesso!");
        enterAs(selected.id, selected.name ?? ownerName);
      } else {
        const res = await verify({ data: { staffId: selected.id, pin } });
        if (res.ok) {
          const isOwnerProfile = selected.id === ownerId;
          if (isOwnerProfile && pinIsDefault) {
            setAfterDefaultLogin({ id: selected.id, name: res.staff.name ?? selected.name });
            setSelected(null);
            setPin("");
            return;
          }
          enterAs(selected.id, res.staff.name ?? selected.name);
        }
      }
    } catch (err: any) {
      toast.error(err?.message || "Erro ao validar PIN");
    } finally {
      setBusy(false);
    }
  };

  const onForgotSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (!/^\d{4,6}$/.test(newPin)) throw new Error("O PIN deve ter de 4 a 6 dígitos numéricos.");
      if (newPin !== newPinConfirm) throw new Error("Os PINs não conferem.");
      if (forgotMode === "password") {
        await resetWithPassword({ data: { password: accountPassword, newPin } });
      } else {
        await resetWithCode({ data: { code: emailCode, newPin } });
      }
      toast.success("PIN redefinido com sucesso! Use o novo PIN para entrar.");
      setPinIsDefault(false);
      setForgotOpen(false);
      setAccountPassword("");
      setEmailCode("");
      setNewPin("");
      setNewPinConfirm("");
    } catch (err: any) {
      toast.error(err?.message || "Não foi possível redefinir o PIN.");
    } finally {
      setBusy(false);
    }
  };

  const onSendCode = async () => {
    setBusy(true);
    try {
      const res: any = await requestCode({ data: undefined as any });
      toast.success(`Código enviado para ${res?.email ?? "seu e-mail"}.`);
      setForgotMode("code");
    } catch (err: any) {
      toast.error(err?.message || "Não foi possível enviar o código.");
    } finally {
      setBusy(false);
    }
  };


  const logoutFlow = useStaffLogoutFlow();
  const onSignOut = logoutFlow.requestSignOut;



  const sorted = [...profiles].sort((a, b) => {
    if (a.id === ownerId) return -1;
    if (b.id === ownerId) return 1;
    return (a.name ?? "").localeCompare(b.name ?? "");
  });

  return (
    <div className="min-h-[80vh] px-4 py-6 relative">
      <div className="absolute top-2 right-4">
        <Button variant="ghost" size="sm" onClick={onSignOut}>
          <LogOut className="h-4 w-4 mr-2" /> Sair do Sistema
        </Button>
      </div>

      <div className="max-w-4xl mx-auto text-center pt-12">
        <h1 className="text-3xl sm:text-4xl font-bold mb-2">Quem está acessando?</h1>
        <p className="text-muted-foreground mb-6">
          Selecione seu perfil e informe o PIN pessoal para entrar.
        </p>

        {pinIsDefault && (
          <Card className="p-4 mb-8 max-w-2xl mx-auto border-amber-400 bg-amber-50 dark:bg-amber-950/30">
            <p className="text-sm font-bold uppercase tracking-wide text-amber-800 dark:text-amber-300">
              NO ACESSO INICIAL O CARD CRIADO É DO PROPRIETÁRIO. UTILIZE O PIN 1234 PARA ENTRAR.
            </p>
            <div className="flex flex-wrap justify-center gap-2 mt-3">
              <Button
                size="sm"
                onClick={() => {
                  const owner = profiles.find((p) => p.id === ownerId);
                  if (owner) onClickProfile({ ...owner, hasPin: true });
                }}
              >
                Entrar
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  setForgotMode("password");
                  setForgotOpen(true);
                }}
              >
                Alterar PIN
              </Button>
            </div>
          </Card>
        )}

        {isOwnerAccount && !pinIsDefault && (
          <div className="mb-8">
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setForgotMode("password");
                setForgotOpen(true);
              }}
            >
              Alterar meu PIN
            </Button>
          </div>
        )}




        {loading ? (
          <div className="flex justify-center gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-44 w-36 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        ) : sorted.length === 0 ? (
          <Card className="p-8 max-w-md mx-auto">
            <p className="text-muted-foreground">Nenhum perfil cadastrado.</p>
          </Card>
        ) : (
          <div className="flex flex-wrap justify-center gap-6">
            {sorted.map((p) => {
              const isOwner = p.id === ownerId;
              return (
                <div key={p.id} className="w-36 text-center">
                  <button
                    type="button"
                    onClick={() => onClickProfile(p)}
                    className="group w-full text-center"
                  >
                    <Card
                      className={`p-4 hover:border-primary hover:shadow-md transition-all ${
                        p.isBootstrapAdmin ? "border-primary/50" : ""
                      }`}
                    >
                      <Avatar className="h-20 w-20 mx-auto mb-3">
                        {p.photo_url ? <AvatarImage src={p.photo_url} /> : null}
                        <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                          {(p.name ?? "?").charAt(0).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <p className="font-semibold text-sm truncate">{p.name ?? "Sem nome"}</p>
                      <p className="text-xs text-muted-foreground capitalize mt-1">
                        {isOwner ? "Proprietário" : roleLabel(p.role)}
                      </p>
                      {p.isBootstrapAdmin ? (
                        p.hasPin ? (
                          <p className="text-[10px] text-emerald-600 mt-1 font-semibold uppercase">
                            PIN CADASTRADO
                          </p>
                        ) : (
                          <p className="text-[10px] text-amber-600 mt-1 font-bold uppercase leading-tight">
                            SEM PIN CADASTRADO — DEFINA UM PIN DE 4 DÍGITOS
                          </p>
                        )
                      ) : (
                        !p.hasPin && (
                          <p className="text-[10px] text-amber-600 mt-1">PIN não definido</p>
                        )
                      )}
                    </Card>
                  </button>
                  {p.isBootstrapAdmin && isOwnerAccount && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-1 text-xs text-muted-foreground"
                      disabled={busy}
                      onClick={() => onDeactivateAdmin(p.id)}
                    >
                      Inativar
                    </Button>
                  )}
                </div>
              );
            })}

          </div>
        )}
      </div>

      <Dialog
        open={!!selected}
        onOpenChange={(o) => {
          if (!o) {
            setSelected(null);
            setPin("");
            setPinConfirm("");
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Defina seu PIN" : `Entrar como ${selected?.name ?? ""}`}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Este é seu primeiro acesso. Crie um PIN pessoal de 4 a 6 dígitos. Ninguém mais, nem o proprietário, terá acesso a este PIN."
                : "Digite seu PIN pessoal para continuar."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                autoFocus
                required
                maxLength={6}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                placeholder="••••"
                className="text-center text-2xl tracking-[0.4em]"
              />
            </div>
            {mode === "create" && (
              <div className="space-y-2">
                <Label>Confirme o PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  required
                  maxLength={6}
                  value={pinConfirm}
                  onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, ""))}
                  placeholder="••••"
                  className="text-center text-2xl tracking-[0.4em]"
                />
              </div>
            )}
            {selected?.id === ownerId && mode === "verify" && (
              <button
                type="button"
                className="text-xs text-primary underline"
                onClick={() => {
                  setSelected(null);
                  setForgotMode("password");
                  setForgotOpen(true);
                }}
              >
                Esqueci meu PIN
              </button>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSelected(null)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Aguarde..." : mode === "create" ? "Salvar PIN e entrar" : "Entrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Após entrar com o PIN padrão */}
      <Dialog open={!!afterDefaultLogin} onOpenChange={(o) => !o && setAfterDefaultLogin(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Altere seu PIN</DialogTitle>
            <DialogDescription className="uppercase font-semibold">
              VOCÊ ENTROU COM O PIN INICIAL 1234. RECOMENDAMOS CRIAR UM PIN PESSOAL AGORA.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                const target = afterDefaultLogin;
                setAfterDefaultLogin(null);
                if (target) enterAs(target.id, target.name);
              }}
            >
              Continuar assim
            </Button>
            <Button
              onClick={() => {
                setAfterDefaultLogin(null);
                setForgotMode("password");
                setForgotOpen(true);
              }}
            >
              Modificar PIN
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Redefinir PIN do proprietário */}
      <Dialog open={forgotOpen} onOpenChange={(o) => setForgotOpen(o)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Redefinir meu PIN</DialogTitle>
            <DialogDescription>
              {forgotMode === "password"
                ? "Confirme a senha da sua conta (a mesma do login por e-mail) e escolha um novo PIN."
                : "Digite o código de 6 dígitos enviado para o e-mail da sua conta e escolha um novo PIN."}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onForgotSubmit} className="space-y-4">
            {forgotMode === "password" ? (
              <div className="space-y-2">
                <Label>Senha da conta</Label>
                <Input
                  type="password"
                  required
                  value={accountPassword}
                  onChange={(e) => setAccountPassword(e.target.value)}
                />
              </div>
            ) : (
              <div className="space-y-2">
                <Label>Código recebido por e-mail</Label>
                <Input
                  inputMode="numeric"
                  required
                  maxLength={6}
                  value={emailCode}
                  onChange={(e) => setEmailCode(e.target.value.replace(/\D/g, ""))}
                  className="text-center text-2xl tracking-[0.4em]"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label>Novo PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                required
                maxLength={6}
                value={newPin}
                onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-[0.4em]"
              />
            </div>
            <div className="space-y-2">
              <Label>Confirme o novo PIN</Label>
              <Input
                type="password"
                inputMode="numeric"
                required
                maxLength={6}
                value={newPinConfirm}
                onChange={(e) => setNewPinConfirm(e.target.value.replace(/\D/g, ""))}
                className="text-center text-2xl tracking-[0.4em]"
              />
            </div>
            <button
              type="button"
              className="text-xs text-primary underline"
              disabled={busy}
              onClick={() => (forgotMode === "password" ? onSendCode() : setForgotMode("password"))}
            >
              {forgotMode === "password"
                ? "Entrei com Google ou não lembro a senha — enviar código por e-mail"
                : "Usar a senha da conta"}
            </button>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setForgotOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={busy}>
                {busy ? "Aguarde..." : "Salvar novo PIN"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <StaffLogoutNoticeDialog
        open={logoutFlow.open}
        onOpenChange={logoutFlow.setOpen}
        onConfirm={logoutFlow.confirmSignOut}
      />
    </div>
  );
}
